import type { Channel, InboxMacro } from '@sbc/domain-models/gateway';
import { replyMaxLength } from '@sbc/domain-models/gateway';
import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useInboxMacros } from '@/lib/api/inbox';
import { cn } from '@/lib/utils';

import { CHANNEL_NAMES } from './inbox-format';
import { fillMacro, macroPreview, searchMacros } from './inbox-macros';

export function MacroPicker({
  channel,
  firstName,
  onPick,
  onClose,
}: {
  channel: Channel;
  firstName: string | null;
  onPick: (text: string) => void;
  onClose: () => void;
}) {
  const macros = useInboxMacros(true);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const matches = searchMacros(macros.data?.macros ?? [], query);
  const listId = useId();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const pick = (macro: InboxMacro | undefined) => {
    if (macro) {
      onPick(fillMacro(macro.body, firstName));
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex items-center gap-2">
        <Input
          ref={searchRef}
          aria-label="Search macros"
          aria-controls={listId}
          placeholder="Search macros by name or text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((index) => Math.max(Math.min(index + 1, matches.length - 1), 0));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault();
              pick(matches[activeIndex]);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            }
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          className="size-8 shrink-0 px-0"
          aria-label="Close macros"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>
      <p className="sr-only" aria-live="polite">
        {matches[activeIndex]?.name ?? ''}
      </p>
      <MacroList
        listId={listId}
        macros={matches}
        loading={macros.isPending}
        error={macros.isError ? macros.error.message : null}
        searching={query.trim().length > 0}
        activeIndex={activeIndex}
        channel={channel}
        firstName={firstName}
        onHover={setActiveIndex}
        onPick={pick}
      />
    </div>
  );
}

export function MacroList({
  listId,
  macros,
  loading,
  error,
  searching,
  activeIndex,
  channel,
  firstName,
  onHover,
  onPick,
}: {
  listId: string;
  macros: readonly InboxMacro[];
  loading: boolean;
  error: string | null;
  searching: boolean;
  activeIndex: number;
  channel: Channel;
  firstName: string | null;
  onHover: (index: number) => void;
  onPick: (macro: InboxMacro) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const movedByPointer = useRef(false);
  const maxLength = replyMaxLength(channel);

  useEffect(() => {
    // Scrolling to a row the pointer just entered would slide the next row under it.
    if (movedByPointer.current) {
      movedByPointer.current = false;
      return;
    }
    listRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (loading) {
    return <p className="px-1 text-xs text-[var(--color-muted-foreground)]">Loading macros…</p>;
  }
  if (error) {
    return <p className="px-1 text-xs text-[var(--color-destructive)]">{error}</p>;
  }
  if (macros.length === 0) {
    return (
      <p className="px-1 text-xs text-[var(--color-muted-foreground)]">
        {searching ? 'No macro matches.' : 'No macros synced from Intercom yet.'}
      </p>
    );
  }
  return (
    <ul
      ref={listRef}
      id={listId}
      aria-label="Macros"
      className="max-h-56 space-y-0.5 overflow-y-auto"
    >
      {macros.map((macro, index) => {
        const length = fillMacro(macro.body, firstName).trim().length;
        const tooLong = length > maxLength;
        return (
          <li key={macro.id}>
            <button
              type="button"
              aria-current={index === activeIndex ? 'true' : undefined}
              className={cn(
                'block w-full rounded px-2 py-1.5 text-left text-sm',
                index === activeIndex && 'bg-[var(--color-accent)]',
              )}
              onMouseEnter={() => {
                movedByPointer.current = true;
                onHover(index);
              }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(macro)}
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate font-medium">{macro.name}</span>
                <span
                  className={cn(
                    'shrink-0 text-[11px]',
                    tooLong
                      ? 'text-[var(--color-destructive)]'
                      : 'text-[var(--color-muted-foreground)]',
                  )}
                >
                  {tooLong ? `Too long for ${CHANNEL_NAMES[channel]} · ` : ''}
                  {length} chars
                </span>
              </span>
              <span className="block truncate text-xs text-[var(--color-muted-foreground)]">
                {macroPreview(macro.body)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
